import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { AnimatePresence, motion, useAnimation } from 'framer-motion';
import { useEffect } from 'react';
import classnames from 'classnames';
import { Input } from '@che-ins-ui/input';
import { TriangleIcon } from '@che-ins-ui/icons/TriangleIcon';
import { InputError } from '@/components/common/InputError';
import { CircleLoader } from '@/components/common/CircleLoader/CircleLoader';
import { useCheTheme } from '@/styles/themes';
import { useAutocomplete } from './hooks';
import { useStyles } from './styles';
const PlaneIcon = () => (_jsx("svg", { viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: _jsx("path", { d: "M21 16v-2l-8-5V3.5A1.5 1.5 0 0 0 11.5 2 1.5 1.5 0 0 0 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5Z", fill: "currentColor" }) }));
const PinIcon = () => (_jsx("svg", { viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: _jsx("path", { d: "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7Zm0 9.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5Z", fill: "currentColor" }) }));
export const Autocomplete = ({ type, value, code, onChange, onBlur, error, label, placeholder, }) => {
    const classes = useStyles();
    const theme = useCheTheme();
    const { rootRef, inputValue, suggestions, isOpen, isLoading, activeIndex, handleInputChange, handleFocus, handleBlur, handleKeyDown, handleDropdownMouseDown, handleSuggestionMouseDown, handleSuggestionClick, handleSuggestionTouchStart, handleSuggestionTouchMove, handleSuggestionTouchEnd, handleSuggestionTouchCancel, } = useAutocomplete({
        type,
        value,
        onChange,
        onBlur,
    });
    const chevronControls = useAnimation();
    useEffect(() => {
        if (isOpen) {
            const sequence = async () => {
                await chevronControls.start({
                    y: 6,
                    transition: { duration: 0.075 },
                });
                await chevronControls.start({
                    y: 0,
                    rotate: 180,
                    transition: { duration: 0.15 },
                });
            };
            sequence();
        }
        else {
            chevronControls.start({
                rotate: 0,
                transition: { duration: 0.175 },
            });
        }
    }, [isOpen, chevronControls]);
    const Icon = type === 'airport' ? PinIcon : PlaneIcon;
    const postfix = code ? (_jsx("span", { className: classes.codePostfix, children: code })) : (_jsx("button", { className: classes.dropdownButton, onMouseDown: handleDropdownMouseDown, tabIndex: -1, children: _jsx(motion.div, { animate: chevronControls, children: _jsx(TriangleIcon, {}) }) }));
    const renderDropdownContent = () => {
        if (isLoading) {
            return (_jsx("div", { className: classes.loaderContainer, children: _jsx(CircleLoader, { boxSize: 20, color: theme.colors.textSecondary }) }));
        }
        if (suggestions.length === 0) {
            return _jsx("div", { className: classes.empty, children: "\u041D\u0438\u0447\u0435\u0433\u043E \u043D\u0435 \u043D\u0430\u0439\u0434\u0435\u043D\u043E" });
        }
        return suggestions.map((item, index) => (_jsxs("button", { className: classnames(classes.item, {
                [classes.itemActive]: index === activeIndex,
            }), onMouseDown: (e) => handleSuggestionMouseDown(e, item), onTouchStart: handleSuggestionTouchStart, onTouchMove: handleSuggestionTouchMove, onTouchEnd: () => handleSuggestionTouchEnd(item), onTouchCancel: handleSuggestionTouchCancel, onClick: () => handleSuggestionClick(item), children: [_jsx("div", { className: classes.itemIcon, children: _jsx(Icon, {}) }), _jsxs("div", { className: classes.itemContent, children: [_jsxs("div", { className: classes.itemMain, children: [_jsx("span", { className: classes.itemName, children: item.name }), _jsx("span", { className: classes.itemCode, children: item.code })] }), item.cityName && (_jsx("div", { className: classes.itemCity, children: item.cityName }))] })] }, `${item.code}-${item.name}`)));
    };
    return (_jsxs("div", { className: classes.root, ref: rootRef, children: [_jsxs("div", { className: classes.inputWrapper, children: [_jsx(Input, { value: inputValue, onChange: handleInputChange, onFocus: handleFocus, onBlur: handleBlur, onKeyDown: handleKeyDown, label: label, placeholder: placeholder, shouldFitContent: true, invalid: Boolean(error), autoComplete: "off", postfix: postfix }), _jsx(AnimatePresence, { children: isOpen && (_jsx(motion.div, { className: classes.dropdownWrapper, initial: { opacity: 0, y: -8 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: -8 }, transition: { duration: 0.18, ease: 'easeOut' }, children: _jsx("div", { className: classes.dropdown, children: renderDropdownContent() }) })) })] }), _jsx(InputError, { error: error })] }));
};
